import axios from "axios";
import { some } from "lodash";
import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import FilterForm from "./FilterForm";

import {
  handleChange as handleChangeAction,
  handleChangeToInitState as handleChangeToInitStateAction,
} from "../../../actions/handleChangeAction";
import { getKlageKoder } from "../../../actions/klagekoderAction";
import { getMiljogebyrer as getMiljogebyrerAction } from "../../../actions/miljogebyrerActions";
import { getMiljoleveringstyper as getMiljoleveringstyperAction } from "../../../actions/miljoleveringstyperActions";
import { getOvertredelseskoderMiljogebyr as getOvertredelseskoderMiljogebyrAction } from "../../../actions/overtredelseskoderMiljogebyrActions";
import { getVedtakskoder } from "../../../actions/vedtakskoderAction";

import { INITIAL_STATE as miljogebyrerOversiktFilterFormInitState } from "../../../reducers/forms/miljogebyrerOversiktFilterFormReducer";

import { getSelectOptionsEnhanced } from "../../../selectors/selectOptionsSelector";
import * as inputValidation from "../../../utils/inputValidationUtils";
import { IsValidated } from "../../../utils/validationUtils";

import {
  setPage as setPageAction,
  setSort as setSortAction,
} from "../../../actions/advancedListActions";
import { miljogebyrStatuser } from "../../../constants/globals";
import { replaceWhitespacesWithEmptyStrings } from "../../../utils/sanitizationUtils";

import { stedfortrederDriftsleder } from "../../../constants/roles";
import { authorized } from "../../../utils/authorizationUtils";

const rules = {
  ileggelsesnummer: "isPositiveInteger",
  tjenestenummer: "isPositiveInteger",
};

const authorizedstedfortrederDriftsleder = authorized([
  stedfortrederDriftsleder,
]);

function getMiljogebyrerFilter(filterform) {
  let { selectedStatuser, ...filter } = filterform;

  filter = {
    ...filter,
    fraDato: filter.fraDato && filter.fraDato.toISOString(),
    tilDato: filter.tilDato && filter.tilDato.toISOString(),
  };

  miljogebyrStatuser.forEach(
    (status) =>
      (filter[status.id] = miljogebyrerOversiktFilterFormInitState[status]),
  );
  selectedStatuser.forEach((selected) => (filter[selected.id] = true));
  delete filter.validationError;
  return filter;
}

export default function FilterContainer() {
  const [showSokSpinner, setShowSokSpinner] = useState(false);
  const axiosCancelTokenSource = useRef(null);
  const dispatch = useDispatch();

  const miljogebyrerOversiktFilterForm = useSelector(
    (state) => state.forms.miljogebyrerOversiktFilterForm,
  );
  const overtredelseskoderMiljogebyr = useSelector(
    (state) => state.overtredelseskoderMiljogebyr,
  );
  const miljoleveringstyper = useSelector((state) =>
    getSelectOptionsEnhanced(state, "miljoleveringstyper", "navn", "id"),
  );
  const betjentgrupper = useSelector((state) =>
    getSelectOptionsEnhanced(state, "betjentgrupper", "navn", "id", {
      text: "Velg",
      value: "",
    }),
  );
  const miljogebyrer = useSelector((state) => state.miljogebyrer);
  const klagekoder = useSelector((state) => state.klagekoder);
  const vedtakskoder = useSelector((state) => state.vedtakskoder);

  const handleChangeDispatch = (variableName, value) =>
    dispatch(
      handleChangeAction(
        "MILJOGEBYRER_OVERSIKT_FILTER_FORM",
        variableName,
        value,
      ),
    );

  useEffect(() => {
    dispatch(getOvertredelseskoderMiljogebyrAction());
    dispatch(getMiljoleveringstyperAction());

    if (!authorizedstedfortrederDriftsleder) {
      dispatch(getKlageKoder());
      dispatch(getVedtakskoder());
    }
    return () => {
      axiosCancelTokenSource.current && axiosCancelTokenSource.current.cancel();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getPropsAsFakeState = () => ({
    ileggelsesnummer: miljogebyrerOversiktFilterForm.ileggelsesnummer,
    tjenestenummer: miljogebyrerOversiktFilterForm.tjenestenummer,
    saksnummer: miljogebyrerOversiktFilterForm.saksnummer,
    validationError: {
      ileggelsesnummer:
        miljogebyrerOversiktFilterForm.validationError.ileggelsesnummer,
      tjenestenummer:
        miljogebyrerOversiktFilterForm.validationError.tjenestenummer,
    },
  });

  const handleChange = (e) => {
    if (e.target.name === "selectedStatuser") {
      miljogebyrStatuser.forEach((miljogebyrStatus) => {
        if (some(e.target.value, miljogebyrStatus)) {
          handleChangeDispatch(miljogebyrStatus.id, true);
        } else {
          handleChangeDispatch(miljogebyrStatus.id, null);
        }
      });
    }
    handleChangeDispatch(e.target.name, e.target.value);
  };

  const handleBlur = (e) => {
    if (
      e.target.name === "ileggelsesnummer" ||
      e.target.name === "tjenestenummer" ||
      e.target.name === "saksnummer"
    ) {
      handleChangeDispatch(
        e.target.name,
        replaceWhitespacesWithEmptyStrings(e.target.value),
      );
    }
  };

  const handleChangeToInitState = () => {
    axiosCancelTokenSource.current && axiosCancelTokenSource.current.cancel();
    dispatch(
      handleChangeToInitStateAction("MILJOGEBYRER_OVERSIKT_FILTER_FORM"),
    );
  };

  const isValid = () => {
    const propsAsFakeState = getPropsAsFakeState();
    const { isValid: valid, validationError } = IsValidated(
      propsAsFakeState,
      rules,
    );

    handleChangeDispatch(
      "validationError.ileggelsesnummer",
      validationError.ileggelsesnummer,
    );
    handleChangeDispatch(
      "validationError.tjenestenummer",
      validationError.tjenestenummer,
    );

    const fraDateTimeTilDateTimeValid =
      inputValidation.fraDateTimeTilDateTimeValidation(
        miljogebyrerOversiktFilterForm,
        handleChangeDispatch,
      );

    const fraOgTilIneOverforingsdatoValidationErrors =
      inputValidation.getDateTimeValidationError({
        fraDato: miljogebyrerOversiktFilterForm.fraIneOverforingsdato,
        tilDato: miljogebyrerOversiktFilterForm.tilIneOverforingsdato,
      });
    handleChangeDispatch(
      "fraIneOverforingsdatoValidationErrorText",
      fraOgTilIneOverforingsdatoValidationErrors.fraDatoValidationErrorText,
    );
    handleChangeDispatch(
      "tilIneOverforingsdatoValidationErrorText",
      fraOgTilIneOverforingsdatoValidationErrors.tilDatoValidationErrorText,
    );

    const fraOgTilOverforingsdatoIneValid = !(
      fraOgTilIneOverforingsdatoValidationErrors.fraDatoValidationErrorText ||
      fraOgTilIneOverforingsdatoValidationErrors.tilDatoValidationErrorText
    );

    return (
      valid && fraDateTimeTilDateTimeValid && fraOgTilOverforingsdatoIneValid
    );
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (isValid()) {
      setShowSokSpinner(true);
      axiosCancelTokenSource.current = axios.CancelToken.source();

      const page = 1;
      dispatch(
        getMiljogebyrerAction(
          getMiljogebyrerFilter(miljogebyrerOversiktFilterForm),
          page,
          miljogebyrer.count,
          axiosCancelTokenSource.current.token,
        ),
      )
        .then(() => {
          setShowSokSpinner(false);
          dispatch(setPageAction("miljogebyrer", page));
          dispatch(
            setSortAction("miljogebyrer", {
              cellKey: "ileggelsesdatoTil",
              orders: "desc",
            }),
          );
        })
        .catch(() => setShowSokSpinner(false));
    }
  };

  return (
    <div className="ileggelse-filter-container">
      <h1>Søk etter miljøgebyr</h1>
      <FilterForm
        showSokSpinner={showSokSpinner}
        miljogebyrFilterState={miljogebyrerOversiktFilterForm}
        overtredelseskoderMiljogebyr={overtredelseskoderMiljogebyr}
        miljogebyrStatuser={miljogebyrStatuser}
        miljoleveringstyper={miljoleveringstyper}
        betjentgrupper={betjentgrupper}
        vedtakskoder={vedtakskoder}
        klagekoder={klagekoder}
        handleChange={handleChange}
        handleBlur={handleBlur}
        handleChangeToInitState={handleChangeToInitState}
        handleSubmit={handleSubmit}
      />
    </div>
  );
}
